import { inject, Injectable } from '@angular/core';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { UserKeys } from '@config/keys/user.keys';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { UserApiService } from '@core/services/api/user/user-api.service';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { AuthStoreDispatcher } from '@common/dispatchers/authentication.dispatcher';
import { computeTokenExpiresAt, peekJwtSubject } from '@core/helpers/dood-anonymous-jwt.helper';
import { AnonymousUpgradeSessionService } from '@core/services/auth/anonymous-upgrade-session.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _user: DoodUserModel | null = null;
  private readonly analyticsService = inject(AnalyticsService);

  get user(): DoodUserModel | null {
    return this._user;
  }

  constructor(
    private authSelector: AuthStoreSelector,
    private authDispatcher: AuthStoreDispatcher,
    private readonly userApiService: UserApiService,
    private readonly anonymousUpgradeSession: AnonymousUpgradeSessionService,
  ) {
    this.authSelector.selectUser$.subscribe(user => {
      this._user = user;
    });
  }

  setUser(user: DoodUserModel): void {
    this.authDispatcher.fetchUserSuccess(user);
  }

  /**
   * GET /users/me, then store the user — unless the session token changed identity while the
   * request was in flight. A late response for a previous token (e.g. a guest minted by a
   * concurrent bootstrap) must not overwrite the user of the current token (DOOD-3495).
   */
  loadUser$(): Observable<DoodUserModel> {
    return defer(() => {
      const requestIdentity = this.currentTokenIdentity();
      return this.userApiService.getUserApi$().pipe(
        tap(user => {
          if (this.currentTokenIdentity() !== requestIdentity) {
            this.analyticsService.log({
              level: 'warn',
              label: 'user_load_stale_token_ignored',
              message: { userId: user?.id ?? null },
            });
            return;
          }
          this.setUser(user);
        }),
      );
    });
  }

  /** JWT subject when readable (stable across Firebase token refreshes), else the raw token. */
  private currentTokenIdentity(): string | null {
    const token = this.authSelector.selectStatus().token;
    return peekJwtSubject(token) ?? token;
  }

  loadUserPromise(): Promise<DoodUserModel | undefined> {
    return this.userApiService.getUserPromise();
  }

  /**
   * Create a Dood guest via POST /users/anonymous (no Authorization).
   * Stores the returned access_token + local expiry, then hydrates the user.
   */
  createAnonymousUser$(): Observable<DoodUserModel> {
    return this.userApiService.createAnonymousUserApi$().pipe(
      take(1),
      tap(createdUser => {
        if (createdUser.access_token) {
          this.authDispatcher.updateStatus({
            token: createdUser.access_token,
            tokenExpiresAt: computeTokenExpiresAt(createdUser.expires_in ?? 21600),
          });
        }
      }),
      map(createdUser => {
        const {
          access_token: _accessToken,
          token_type: _tokenType,
          expires_in: _expiresIn,
          ...user
        } = createdUser;
        this.setUser(user as DoodUserModel);
        return user as DoodUserModel;
      }),
    );
  }

  createUser$(user: Partial<DoodUserModel>): Observable<DoodUserModel> {
    user[UserKeys.Email] = user[UserKeys.Email]?.toLowerCase();
    return this.userApiService.createUserApi$(user).pipe(
      take(1),
      tap(createdUser => this.setUser(createdUser)),
    );
  }

  /**
   * Upgrade guest → real account with both Authorization headers, then switch to the Firebase ID token.
   */
  upgradeAnonymousUser$(
    user: Partial<DoodUserModel>,
    userId: string,
    firebaseIdToken: string,
  ): Observable<DoodUserModel> {
    user[UserKeys.Email] = user[UserKeys.Email]?.toLowerCase();
    return this.userApiService.upgradeAnonymousUserApi$(user, userId, firebaseIdToken).pipe(
      take(1),
      tap(updatedUser => {
        this.anonymousUpgradeSession.finish(firebaseIdToken);
        this.setUser(updatedUser);
      }),
      catchError(error => {
        this.anonymousUpgradeSession.cancel();
        return throwError(() => error);
      }),
    );
  }

  /**
   * Persist a Dood anonymous JWT from Como (or order) responses.
   */
  setDoodAnonymousAccessToken(accessToken: string, expiresInSeconds = 21600): void {
    this.authDispatcher.updateStatus({
      token: accessToken,
      tokenExpiresAt: computeTokenExpiresAt(expiresInSeconds),
    });
  }

  updateUser$(user: Partial<DoodUserModel>, id: string): Observable<DoodUserModel> {
    const _user = { ...user, email: user.email?.toLowerCase() };
    return this.userApiService
      .updatePersonalInfo$(_user, id)
      .pipe(tap(updatedUser => this.setUser(updatedUser)));
  }
}
